import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DollarSign } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { API_BASE } from '../../utils/constants';

const LoginPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'signup') {
      setIsSignUp(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        // Validation for signup
        if (!formData.name || formData.name.trim().length < 2) {
          if (window.showNotification) {
            window.showNotification('error', 'Name must be at least 2 characters long');
          }
          setLoading(false);
          return;
        }

        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
          if (window.showNotification) {
            window.showNotification('error', 'Please enter a valid email address');
          }
          setLoading(false);
          return;
        }

        if (!formData.password || formData.password.length < 6) {
          if (window.showNotification) {
            window.showNotification('error', 'Password must be at least 6 characters long');
          }
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_BASE}/users/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        const data = await res.text();
        
        if (data.includes('successfully')) {
          if (window.showNotification) {
            window.showNotification('success', data);
          }
          // Switch to login mode after successful registration
          setIsSignUp(false);
          setFormData({ name: '', email: formData.email, password: '' });
        } else {
          if (window.showNotification) {
            window.showNotification('error', data);
          }
        }
      } else {
        // Login validation
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
          if (window.showNotification) {
            window.showNotification('error', 'Please enter a valid email address');
          }
          setLoading(false);
          return;
        }

        if (!formData.password) {
          if (window.showNotification) {
            window.showNotification('error', 'Password is required');
          }
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_BASE}/users/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, password: formData.password })
        });

        if (res.ok) {
          const user = await res.json();
          login(user);
          if (window.showNotification) {
            window.showNotification('success', `Welcome back, ${user.name}! 👋`);
          }
          navigate('/dashboard');
        } else {
          const errorText = await res.text();
          if (window.showNotification) {
            window.showNotification('error', `Login failed: ${errorText}`);
          }
        }
      }
    } catch (err) {
      console.error('Error:', err);
      if (window.showNotification) {
        window.showNotification('error', 'Something went wrong. Please check your connection and try again.');
      }
    }
    
    setLoading(false);
  };

  // Form validation in real-time
  const isFormValid = () => {
    if (isSignUp) {
      return formData.name?.trim().length >= 2 && 
             formData.email && /\S+@\S+\.\S+/.test(formData.email) && 
             formData.password?.length >= 6;
    } else {
      return formData.email && /\S+@\S+\.\S+/.test(formData.email) && formData.password;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 transition-colors">
            <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-sm">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold">SplitWise</span>
          </Link>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSignUp 
              ? 'Join SplitWise and start managing expenses with friends' 
              : 'Welcome back! Please sign in to continue'
            }
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {isSignUp && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required={isSignUp}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    formData.name && formData.name.trim().length < 2 ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {formData.name && formData.name.trim().length < 2 && (
                  <p className="text-red-500 text-xs mt-1">Name must be at least 2 characters</p>
                )}
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  formData.email && !/\S+@\S+\.\S+/.test(formData.email) ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm`}
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              {formData.email && !/\S+@\S+\.\S+/.test(formData.email) && (
                <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isSignUp ? "new-password" : "current-password"}
                required
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  formData.password && isSignUp && formData.password.length < 6 ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm`}
                placeholder={isSignUp ? "Enter a password (min 6 characters)" : "Enter your password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              {formData.password && isSignUp && formData.password.length < 6 && (
                <p className="text-red-500 text-xs mt-1">Password must be at least 6 characters</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isSignUp ? 'Creating account...' : 'Signing in...'}
                </div>
              ) : (
                isSignUp ? 'Create Account' : 'Sign In'
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setFormData({ name: '', email: '', password: '' });
              }}
              className="text-teal-600 hover:text-teal-500 font-medium transition-colors"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;